import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { ROUTES } from "../../../routes";

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signIn(email, password);
      navigate(ROUTES.VINAI, { replace: true });
    } catch (err: any) {
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen lg:flex">
      <section className="relative hidden flex-1 overflow-hidden bg-accent px-12 py-14 text-[#FFF6EE] lg:flex lg:flex-col lg:justify-between">
        <div className="absolute -right-16 -top-24 h-[360px] w-[360px] rounded-full bg-white/15 blur-[2px]" />
        <div className="absolute -bottom-20 -left-14 h-[280px] w-[280px] rounded-full bg-[#7A2438]/35" />
        <div className="absolute left-[58%] top-[44%] h-[140px] w-[140px] rounded-full bg-[#CC9A3E]/35" />

        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-[16px] bg-[#FFF6EE] font-display text-[24px] font-extrabold text-accent">v</div>
          <span className="font-display text-[28px] font-bold tracking-[-0.05em]">vinIA</span>
        </div>

        <div className="relative max-w-[480px]">
          <h1 className="font-display text-[56px] font-bold leading-[0.98] tracking-[-0.06em]">
            Le vin n'a jamais été aussi facile à raconter.
          </h1>
          <p className="mt-6 text-[18px] leading-8 text-[#FFF6EE]/90">
            Fiches, accords, argumentaires, carte publique. Votre cave, sublimée par l'IA en quelques secondes.
          </p>
        </div>

        <div className="relative flex items-center gap-4 text-[14px] text-[#FFF6EE]/80">
          <span>Fiches dégustation</span>
          <span>·</span>
          <span>Carte QR</span>
          <span>·</span>
          <span>3 langues</span>
        </div>
      </section>

      <section className="flex flex-1 items-center justify-center px-5 py-12 lg:px-10">
        <div className="w-full max-w-[390px]">
          <div className="mb-8">
            <div className="font-display text-[36px] font-bold tracking-[-0.05em] text-foreground">Bon retour</div>
            <p className="mt-2 text-[15px] text-secondary">Connectez-vous à votre studio VinIA.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Input
              label="Mot de passe"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            {error && (
              <p className="rounded-[16px] border border-error/20 bg-error-bg px-4 py-3 text-[13px] text-error">
                {error}
              </p>
            )}

            <Button type="submit" variant="primary" disabled={loading} className="w-full">
              {loading ? "Connexion…" : "Se connecter"}
            </Button>
          </form>

          <p className="mt-6 text-center text-[14px] text-secondary">
            Pas encore de compte ?{" "}
            <Link to={ROUTES.SIGNUP} className="font-semibold text-accent transition-opacity hover:opacity-75">
              Créer un compte
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}

function getErrorMessage(code: string): string {
  switch (code) {
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Email ou mot de passe incorrect.";
    case "auth/too-many-requests":
      return "Trop de tentatives. Réessayez plus tard.";
    case "auth/invalid-email":
      return "Adresse e-mail invalide.";
    default:
      return "Une erreur est survenue. Réessayez.";
  }
}
